import { Link } from 'react-router-dom';
import { FileTextOutlined } from '@ant-design/icons';
import { Highlight } from '@/shared/ui/Highlight';
import { formatName, formatDate } from '@/utils/search';
import type { SearchReport } from '@/models/search';
import styles from '../../GlobalSearch.module.css';

const DROPDOWN_LIMIT = 5;

export const ReportResults = ({
  reports,
  onSelect,
  query,
  highlightIndex = -1,
  highlightOffset = 0,
}: {
  reports: SearchReport[];
  onSelect: (path: string) => void;
  query: string;
  highlightIndex?: number;
  highlightOffset?: number;
}) => {
  if (reports.length === 0) return null;
  const visible = reports.slice(0, DROPDOWN_LIMIT);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <FileTextOutlined className={styles.sectionIcon} />
        Reports
      </div>
      {visible.map((report, idx) => {
        const studyDate = formatDate(report.studyDate, { year: undefined });
        const signedDate =
          report.status === 'FINAL'
            ? formatDate(report.signatureDate, { year: undefined })
            : null;
        const reportPath = `/reports/${report.uuid}/preview`;
        const isActive = highlightIndex === highlightOffset + idx;
        const content = (
          <>
            <div className={`${styles.resultIcon} ${styles.report}`}>
              <FileTextOutlined />
            </div>
            <div className={styles.resultContent}>
              <span className={styles.resultPrimary}>
                <Highlight
                  text={formatName(
                    report.patientFirstName,
                    report.patientLastName
                  )}
                  query={query}
                />
                {report.status && (
                  <span
                    className={`${styles.tag} ${report.status === 'FINAL' ? styles.tagSuccess : styles.tagDefault}`}
                  >
                    {report.status}
                  </span>
                )}
                {report.isAddendum && (
                  <span className={`${styles.tag} ${styles.tagReport}`}>
                    Addendum
                  </span>
                )}
              </span>
              <span className={styles.resultSecondary}>
                {report.studyAccessionNumber && (
                  <>
                    ACC:{' '}
                    <Highlight
                      text={report.studyAccessionNumber}
                      query={query}
                    />
                  </>
                )}
                {report.studyAccessionNumber && studyDate && ' · '}
                {studyDate && <>{studyDate}</>}
                {signedDate && <> · Signed: {signedDate}</>}
              </span>
            </div>
          </>
        );

        if (!report.uuid) {
          return (
            <div key={report.uuid} className={styles.resultItem}>
              {content}
            </div>
          );
        }

        return (
          <Link
            key={report.uuid}
            to={reportPath}
            className={`${styles.resultItem} ${isActive ? styles.resultItemActive : ''}`}
            onClick={() => onSelect(reportPath)}
          >
            {content}
          </Link>
        );
      })}
    </div>
  );
};
